import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { apiKeysApi } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import { Plus, Key, Trash2, Copy, Eye, EyeOff, AlertTriangle, Check } from 'lucide-react';

export default function KeysListPage() {
  const appId = useAppId();
  const { apps, currentApp } = useApps();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState({});
  const [revokeDialog, setRevokeDialog] = useState({ open: false, key: null });
  const [revoking, setRevoking] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState(false);

  const copyAppId = async () => {
    if (currentApp?.appId) {
      await navigator.clipboard.writeText(appId);
      setCopiedAppId(true);
      setTimeout(() => setCopiedAppId(false), 2000);
    }
  };

  useEffect(() => {
    const fetchKeys = async () => {
      if (!currentApp) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiKeysApi.list(appId);
        setApiKeys(response.data?.data);
      } catch (err) {
        console.error('Failed to fetch keys:', err);
        setApiKeys([]);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchKeys();
  }, [currentApp]);

  const toggleKeyVisibility = (keyId) => {
    setShowKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleRevoke = async () => {
    if (!revokeDialog.key || !currentApp) return;

    setRevoking(true);
    try {
      await apiKeysApi.revoke(appId, revokeDialog.key.keyId);
      setApiKeys((prev) =>
        prev.map((k) =>
          k.keyId === revokeDialog.key.keyId ? { ...k, revoked: true } : k
        )
      );
      setRevokeDialog({ open: false, key: null });
    } catch (err) {
      console.error('Failed to revoke key:', err);
    } finally {
      setRevoking(false);
    }
  };

  if (apps.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-center space-y-4">
          <div className="h-20 w-20 rounded-full bg-muted mx-auto flex items-center justify-center">
            <Key className="h-10 w-10 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold">No apps yet</h2>
          <p className="text-muted-foreground max-w-md">
            Create an app first to start managing API keys.
          </p>
          <Button asChild>
            <Link to="/apps/new">Create App</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!currentApp) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-center space-y-4">
          <div className="h-20 w-20 rounded-full bg-muted mx-auto flex items-center justify-center">
            <Key className="h-10 w-10 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-semibold">Select an app</h2>
          <p className="text-muted-foreground max-w-md">
            Choose an app from the sidebar to manage its API keys.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">API Keys</h2>
          <p className="text-muted-foreground">
            Create and manage API keys for authentication
          </p>
        </div>
        <Button asChild>
          <Link to={`/apps/${appId}/keys/new`}>
            <Plus className="mr-2 h-4 w-4" />
            Create Key
          </Link>
        </Button>
      </div>

      {/* App ID */}
      <Card className="border-dashed">
        <CardContent className="flex items-center justify-between py-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">App ID</p>
            <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
              {appId}
            </code>
          </div>
          <Button variant="ghost" size="sm" onClick={copyAppId}>
            {copiedAppId ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Keys List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : apiKeys.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Key className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No API keys</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first API key to authenticate with the TruContext API
            </p>
            <Button asChild>
              <Link to={`/apps/${appId}/keys/new`}>
                <Plus className="mr-2 h-4 w-4" />
                Create API Key
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <Card key={key.keyId}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium">{key.name || 'Unnamed Key'}</p>
                    <Badge variant={key.revoked ? 'destructive' : 'success'}>
                      {key.revoked ? 'Revoked' : 'Active'}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                      {showKeys[key.keyId]
                        ? `${key.keyPrefix}...`
                        : '••••••••••••••••'}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => toggleKeyVisibility(key.keyId)}
                    >
                      {showKeys[key.keyId] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => copyToClipboard(key.keyPrefix + '...')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Created {formatDate(key.createdAt)}
                    {key.lastUsed && ` • Last used ${formatDate(key.lastUsed)}`}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {!key.revoked && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setRevokeDialog({ open: true, key })}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialog.open}
        onOpenChange={(open) => !revoking && setRevokeDialog({ open, key: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span>Revoke API Key</span>
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke "{revokeDialog.key?.name || 'this key'}"?
              This action cannot be undone. Any applications using this key will no
              longer be able to authenticate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevokeDialog({ open: false, key: null })}
              disabled={revoking}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking ? 'Revoking...' : 'Revoke Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
